import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CxmRouteLoader } from "src/components";
import "./cxm-loader-demo.scss";

type Phase = "shown" | "leaving" | "hidden";

/* Mirrors the production sequence in `pages/withdrawal/Withdrawal.tsx`: the
   logo assembles, the glass clears, and the whole show lands inside a second.
   Pass a longer `hold` to inspect the waiting state and the glint. */
const DEFAULTS = { hold: 900, leave: 300, gap: 1000 };

const readMs = (raw: string | null, fallback: number): number => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const MockForm: React.FC = (): JSX.Element => (
  <div className="cxm-loader-demo__card">
    <div className="cxm-loader-demo__line cxm-loader-demo__line--title" />
    <div className="cxm-loader-demo__line cxm-loader-demo__line--hero" />
    <div className="cxm-loader-demo__line" />
    <div className="cxm-loader-demo__line cxm-loader-demo__line--short" />
    <div className="cxm-loader-demo__button" />
  </div>
);

/**
 * Loops the CXM loader forever so the animation can be reviewed without a
 * session: pane in → hold → pane out → pause → again. Light theme on the left,
 * dark on the right, both in sync. The mock form appears exactly when the
 * glass starts clearing, as it does in the real flow, so the reveal shown here
 * is the reveal that ships.
 *
 * Each half is a containing block for `position: fixed` (via `transform`), so
 * the loader fills its half instead of the viewport — no component changes.
 *
 * Tune with `?hold=ms&leave=ms&gap=ms`. Not linked from anywhere; review only.
 */
const CxmLoaderDemo: React.FC = (): JSX.Element => {
  const [params] = useSearchParams();
  const hold = readMs(params.get("hold"), DEFAULTS.hold);
  const leave = readMs(params.get("leave"), DEFAULTS.leave);
  const gap = readMs(params.get("gap"), DEFAULTS.gap);

  const [phase, setPhase] = useState<Phase>("shown");
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const next: Record<Phase, [Phase, number]> = {
      shown: ["leaving", hold],
      leaving: ["hidden", leave],
      hidden: ["shown", gap],
    };
    const [to, ms] = next[phase];
    const timer = window.setTimeout(() => {
      if (to === "shown") setCycle((c) => c + 1);
      setPhase(to);
    }, ms);
    return () => window.clearTimeout(timer);
  }, [phase, hold, leave, gap]);

  const loader =
    phase !== "hidden" ? (
      <CxmRouteLoader key={cycle} label="Preparing your payout" leaving={phase === "leaving"} />
    ) : null;

  return (
    <div className="cxm-loader-demo">
      <section className="cxm-loader-demo__half" data-theme="light">
        {phase !== "shown" && <MockForm />}
        {loader}
        <span className="cxm-loader-demo__tag">light</span>
      </section>

      <section className="cxm-loader-demo__half" data-theme="dark">
        {phase !== "shown" && <MockForm />}
        {loader}
        <span className="cxm-loader-demo__tag">dark</span>
      </section>

      <div className="cxm-loader-demo__hud">
        cycle {cycle + 1} · {phase} · hold {hold}ms · leave {leave}ms · gap {gap}ms
      </div>
    </div>
  );
};

export default CxmLoaderDemo;
